import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Heart, Share2, Trash2, Star, Upload, Globe, Lock, Copy, MessageCircle, Send, Bold, Italic, Link2, Video, Image as ImageIcon, X, Play } from "lucide-react";
import DOMPurify from "dompurify";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function getYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

function ShareDialog({ open, onClose, post }) {
  const shareUrl = `${window.location.origin}/post/${post?.id}`;
  const shareText = `${post?.caption?.slice(0, 100) || "Check out this post from Kalemash Studio!"}\n\n`;
  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied!"); };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#5C3A2E]">Share Post</DialogTitle>
          <DialogDescription className="text-[#2B2B2B]/50 text-sm">Share this post with others</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + shareUrl)}`, "_blank")} data-testid="share-whatsapp" className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition-colors">
            <MessageCircle className="w-5 h-5 text-[#25D366]" /><span className="text-sm font-medium text-[#2B2B2B]">WhatsApp</span>
          </button>
          <button onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank")} data-testid="share-facebook" className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#1877F2]/10 hover:bg-[#1877F2]/20 transition-colors">
            <Globe className="w-5 h-5 text-[#1877F2]" /><span className="text-sm font-medium text-[#2B2B2B]">Facebook</span>
          </button>
          <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied! Paste it in your Instagram story or bio."); }} data-testid="share-instagram" className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#E4405F]/10 hover:bg-[#E4405F]/20 transition-colors">
            <Heart className="w-5 h-5 text-[#E4405F]" /><span className="text-sm font-medium text-[#2B2B2B]">Instagram</span>
          </button>
          <button onClick={copyLink} data-testid="share-copy-link" className="w-full flex items-center gap-3 p-3 rounded-xl bg-[#EADBC8]/30 hover:bg-[#EADBC8]/50 transition-colors">
            <Copy className="w-5 h-5 text-[#5C3A2E]" /><span className="text-sm font-medium text-[#2B2B2B]">Copy Link</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PostCard({ post, currentUserId, isAdmin, userRole, onLike, onDelete, onFeature, onShare, onComment, onDeleteComment }) {
  const liked = (post.likes || []).includes(currentUserId);
  const avatarUrl = post.author_avatar ? `${API_BASE}/api/files/${post.author_avatar}` : null;
  const [commentText, setCommentText] = useState("");
  const [showComments, setShowComments] = useState(false);
  const comments = post.comments || [];
  const canComment = ["admin", "instructor", "student", "guardian", "spectator", "custom"].includes(userRole);
  const ytId = getYouTubeId(post.video_url);

  const submitComment = () => {
    if (!commentText.trim()) return;
    onComment(post.id, commentText.trim());
    setCommentText("");
  };

  return (
    <div data-testid={`post-card-${post.id}`} className={`bg-white rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border overflow-hidden studio-card ${post.is_featured ? "border-[#C6A969] ring-1 ring-[#C6A969]/20" : "border-[#EADBC8]/30"}`}>
      {post.is_featured && (
        <div className="bg-gradient-to-r from-[#C6A969]/10 to-transparent px-5 py-1.5 flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 text-[#C6A969] fill-[#C6A969]" />
          <span className="text-xs font-medium text-[#C6A969]">Featured</span>
        </div>
      )}
      {/* Author */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {avatarUrl ? <img src={avatarUrl} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-[#EADBC8]/50" /> : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#EADBC8] to-[#C6A969]/30 flex items-center justify-center ring-2 ring-[#EADBC8]/50">
              <span className="text-sm font-semibold text-[#5C3A2E]">{post.author_name?.[0]?.toUpperCase() || "?"}</span>
            </div>
          )}
          <div>
            <span className="text-sm font-semibold text-[#2B2B2B]">{post.author_name}</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-[#2B2B2B]/40">{timeAgo(post.created_at)}</span>
              {post.is_public ? <Globe className="w-2.5 h-2.5 text-[#C6A969]" /> : <Lock className="w-2.5 h-2.5 text-[#2B2B2B]/30" />}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => onFeature(post.id)} className={`rounded-full h-8 w-8 p-0 ${post.is_featured ? "text-[#C6A969]" : "text-[#2B2B2B]/20 hover:text-[#C6A969]"}`}>
              <Star className={`w-4 h-4 ${post.is_featured ? "fill-current" : ""}`} />
            </Button>
          )}
          {(isAdmin || post.author_id === currentUserId) && (
            <Button variant="ghost" size="sm" onClick={() => onDelete(post.id)} className="text-[#2B2B2B]/20 hover:text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
      {/* Title */}
      {post.title && <div className="px-5 pb-1"><h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{post.title}</h3></div>}
      {/* Caption — rendered as rich HTML */}
      {post.caption && (
        <div className="px-5 pb-3">
          <div className="text-sm text-[#2B2B2B] leading-relaxed prose prose-sm max-w-none [&_strong]:font-semibold [&_em]:italic [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.caption) }} />
        </div>
      )}
      {/* Image */}
      {post.image_path && (
        <div className="w-full bg-[#F7F3EF]">
          <img src={`${API_BASE}/api/files/${post.image_path}`} alt="" className="w-full max-h-[500px] object-cover" />
        </div>
      )}
      {/* Video */}
      {ytId && (
        <div className="w-full aspect-video bg-black">
          <iframe src={`https://www.youtube.com/embed/${ytId}`} title="Video" className="w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
        </div>
      )}
      {post.video_url && !ytId && (
        <a href={post.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 mx-5 mb-3 px-4 py-3 bg-[#F7F3EF] rounded-xl hover:bg-[#EADBC8]/40 transition-colors">
          <Play className="w-5 h-5 text-[#5C3A2E]" />
          <span className="text-sm text-[#5C3A2E] truncate">{post.video_url}</span>
        </a>
      )}
      {/* GIF */}
      {post.gif_url && (
        <div className="w-full bg-[#F7F3EF]">
          <img src={post.gif_url} alt="GIF" className="w-full max-h-[400px] object-contain" />
        </div>
      )}
      {/* Actions */}
      <div className="px-5 py-3 flex items-center gap-5 border-t border-[#EADBC8]/20">
        <button data-testid={`like-post-${post.id}`} onClick={() => onLike(post.id)} className="flex items-center gap-1.5 group">
          <Heart className={`w-5 h-5 transition-all duration-200 ${liked ? "text-red-500 fill-red-500 scale-110" : "text-[#2B2B2B]/30 group-hover:text-red-400 group-hover:scale-110"}`} />
          {post.like_count > 0 && <span className={`text-xs ${liked ? "text-red-500 font-medium" : "text-[#2B2B2B]/40"}`}>{post.like_count}</span>}
        </button>
        <button data-testid={`toggle-comments-${post.id}`} onClick={() => setShowComments(!showComments)} className="flex items-center gap-1.5 text-[#2B2B2B]/30 hover:text-[#5C3A2E] transition-colors">
          <MessageCircle className="w-5 h-5" />
          {comments.length > 0 && <span className="text-xs text-[#2B2B2B]/40">{comments.length}</span>}
        </button>
        {post.is_public && (
          <button data-testid={`share-post-${post.id}`} onClick={() => onShare(post)} className="flex items-center gap-1.5 text-[#2B2B2B]/30 hover:text-[#C6A969] transition-colors">
            <Share2 className="w-5 h-5" />
          </button>
        )}
      </div>
      {/* Comments */}
      {showComments && (
        <div className="border-t border-[#EADBC8]/20 px-5 py-3 space-y-3 bg-[#FDFBF9]">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2.5">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#EADBC8] to-[#C6A969]/30 flex items-center justify-center flex-shrink-0 mt-0.5 overflow-hidden">
                {c.author_avatar ? <img src={`${API_BASE}/api/files/${c.author_avatar}`} alt="" className="w-full h-full object-cover" /> : (
                  <span className="text-[10px] font-semibold text-[#5C3A2E]">{c.author_name?.[0]?.toUpperCase()}</span>
                )}
              </div>
              <div className="flex-1 min-w-0 bg-white rounded-xl px-3 py-2 shadow-sm">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#2B2B2B]">{c.author_name}</span>
                  <span className="text-[10px] text-[#2B2B2B]/30">{timeAgo(c.created_at)}</span>
                  {(isAdmin || c.author_id === currentUserId) && (
                    <button onClick={() => onDeleteComment(post.id, c.id)} className="text-red-200 hover:text-red-500 ml-auto transition-colors"><Trash2 className="w-3 h-3" /></button>
                  )}
                </div>
                <p className="text-xs text-[#2B2B2B]/70 mt-0.5 whitespace-pre-wrap">{c.text}</p>
              </div>
            </div>
          ))}
          {canComment && (
            <div className="flex gap-2 pt-1">
              <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Write a comment..." className="rounded-full border-[#EADBC8] text-xs h-9 bg-white" onKeyDown={(e) => e.key === "Enter" && submitComment()} />
              <Button variant="ghost" size="sm" onClick={submitComment} disabled={!commentText.trim()} className="text-[#5C3A2E] rounded-full h-9 w-9 p-0 hover:bg-[#EADBC8]/30">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* Rich text composer */
function PostComposer({ onPost, posting }) {
  const editorRef = useRef(null);
  const [title, setTitle] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [gifUrl, setGifUrl] = useState("");
  const [showMediaPanel, setShowMediaPanel] = useState(null); // "video" | "gif" | null

  const execCmd = (cmd, val) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    const caption = editorRef.current?.innerHTML || "";
    const plainText = editorRef.current?.innerText?.trim() || "";
    if (!plainText && !imageFile && !videoUrl && !gifUrl) { toast.error("Add some content to your post"); return; }
    onPost({ title, caption, imageFile, videoUrl, gifUrl, isPublic });
  };

  const reset = () => {
    setTitle(""); setImageFile(null); setImagePreview(null); setVideoUrl(""); setGifUrl(""); setShowMediaPanel(null);
    if (editorRef.current) editorRef.current.innerHTML = "";
  };

  return (
    <div className="bg-white rounded-2xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 overflow-hidden">
      {/* Title */}
      <input
        data-testid="post-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Post title (optional)"
        className="w-full px-5 pt-5 pb-1 text-lg font-heading font-semibold text-[#5C3A2E] placeholder:text-[#2B2B2B]/20 bg-transparent outline-none"
      />
      {/* Rich editor */}
      <div
        ref={editorRef}
        contentEditable
        data-testid="post-editor"
        className="px-5 py-3 min-h-[80px] max-h-[200px] overflow-y-auto text-sm text-[#2B2B2B] outline-none leading-relaxed [&:empty]:before:content-[attr(data-placeholder)] [&:empty]:before:text-[#2B2B2B]/25"
        data-placeholder="What's on your mind? Share your creative journey..."
      />
      {/* Formatting toolbar */}
      <div className="px-5 py-2 flex items-center gap-1 border-t border-[#EADBC8]/20">
        <button onClick={() => execCmd("bold")} className="p-1.5 rounded-lg hover:bg-[#EADBC8]/30 text-[#5C3A2E]/50 hover:text-[#5C3A2E] transition-colors" title="Bold"><Bold className="w-4 h-4" /></button>
        <button onClick={() => execCmd("italic")} className="p-1.5 rounded-lg hover:bg-[#EADBC8]/30 text-[#5C3A2E]/50 hover:text-[#5C3A2E] transition-colors" title="Italic"><Italic className="w-4 h-4" /></button>
        <div className="w-px h-5 bg-[#EADBC8]/40 mx-1" />
        <label className="p-1.5 rounded-lg hover:bg-[#EADBC8]/30 text-[#5C3A2E]/50 hover:text-[#5C3A2E] transition-colors cursor-pointer" title="Add image">
          <ImageIcon className="w-4 h-4" />
          <input data-testid="post-image-upload" type="file" accept="image/*" onChange={handleImageSelect} className="hidden" />
        </label>
        <button onClick={() => setShowMediaPanel(showMediaPanel === "video" ? null : "video")} className={`p-1.5 rounded-lg hover:bg-[#EADBC8]/30 transition-colors ${showMediaPanel === "video" ? "text-[#C6A969] bg-[#C6A969]/10" : "text-[#5C3A2E]/50 hover:text-[#5C3A2E]"}`} title="Add video">
          <Video className="w-4 h-4" />
        </button>
        <button onClick={() => setShowMediaPanel(showMediaPanel === "gif" ? null : "gif")} className={`p-1.5 rounded-lg hover:bg-[#EADBC8]/30 transition-colors text-xs font-bold ${showMediaPanel === "gif" ? "text-[#C6A969] bg-[#C6A969]/10" : "text-[#5C3A2E]/50 hover:text-[#5C3A2E]"}`} title="Add GIF">
          GIF
        </button>
        <div className="flex-1" />
        <button data-testid="post-privacy-toggle" onClick={() => setIsPublic(!isPublic)}
          className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium transition-all duration-200 ${isPublic ? "bg-[#5C3A2E] text-white" : "bg-[#EADBC8]/30 text-[#2B2B2B]/50"}`}>
          {isPublic ? <Globe className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
          {isPublic ? "Public" : "Private"}
        </button>
      </div>
      {/* Media panel */}
      {showMediaPanel === "video" && (
        <div className="px-5 py-3 border-t border-[#EADBC8]/20 bg-[#FDFBF9] animate-scale-in">
          <div className="flex gap-2">
            <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Paste YouTube or video URL..." className="rounded-xl border-[#EADBC8] text-xs flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setShowMediaPanel(null)} className="rounded-full h-8 w-8 p-0 text-[#2B2B2B]/30 hover:text-[#5C3A2E]"><X className="w-3.5 h-3.5" /></Button>
          </div>
          {videoUrl && getYouTubeId(videoUrl) && (
            <div className="mt-2 aspect-video rounded-xl overflow-hidden bg-black">
              <iframe src={`https://www.youtube.com/embed/${getYouTubeId(videoUrl)}`} title="Preview" className="w-full h-full" frameBorder="0" />
            </div>
          )}
        </div>
      )}
      {showMediaPanel === "gif" && (
        <div className="px-5 py-3 border-t border-[#EADBC8]/20 bg-[#FDFBF9] animate-scale-in">
          <div className="flex gap-2">
            <Input value={gifUrl} onChange={(e) => setGifUrl(e.target.value)} placeholder="Paste GIF URL (e.g., from Giphy)..." className="rounded-xl border-[#EADBC8] text-xs flex-1" />
            <Button variant="ghost" size="sm" onClick={() => setShowMediaPanel(null)} className="rounded-full h-8 w-8 p-0 text-[#2B2B2B]/30 hover:text-[#5C3A2E]"><X className="w-3.5 h-3.5" /></Button>
          </div>
          {gifUrl && <img src={gifUrl} alt="GIF preview" className="mt-2 max-h-48 rounded-xl object-contain" />}
        </div>
      )}
      {/* Image preview */}
      {imagePreview && (
        <div className="px-5 py-3 border-t border-[#EADBC8]/20">
          <div className="relative inline-block rounded-xl overflow-hidden">
            <img src={imagePreview} alt="" className="max-h-40 rounded-xl" />
            <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 transition-colors"><X className="w-3 h-3" /></button>
          </div>
        </div>
      )}
      {/* Submit */}
      <div className="px-5 py-3 border-t border-[#EADBC8]/20 flex justify-end">
        <Button data-testid="submit-post-button" onClick={handleSubmit} disabled={posting} className="bg-[#5C3A2E] text-white rounded-full hover:bg-[#5C3A2E]/90 px-6 h-9 text-sm hover:shadow-[0_4px_16px_rgb(92,58,46,0.25)] transition-all duration-200">
          {posting ? <span className="flex items-center gap-2"><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Posting...</span> : "Post"}
        </Button>
      </div>
    </div>
  );
}

export default function StudioFeed({ layout: Layout }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [sharePost, setSharePost] = useState(null);

  const isAdmin = user?.role === "admin";
  const canCreatePost = ["admin", "instructor", "student", "custom"].includes(user?.role);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/feed"); setPosts(data); } catch { setPosts([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePost = async ({ title, caption, imageFile, videoUrl, gifUrl, isPublic }) => {
    setPosting(true);
    try {
      let image_path = null, image_filename = null;
      if (imageFile) {
        const formData = new FormData();
        formData.append("file", imageFile);
        const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        image_path = data.storage_path;
        image_filename = data.original_filename;
      }
      await api.post("/feed", { title: title || null, caption, image_path, image_filename, video_url: videoUrl || null, gif_url: gifUrl || null, is_public: isPublic });
      toast.success("Posted!");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setPosting(false); }
  };

  const handleLike = async (postId) => { try { await api.post(`/feed/${postId}/like`); load(); } catch { toast.error("Failed to like"); } };
  const handleDelete = async (postId) => { if (!window.confirm("Delete this post?")) return; try { await api.delete(`/feed/${postId}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const handleFeature = async (postId) => { try { await api.post(`/feed/${postId}/feature`); load(); } catch { toast.error("Failed to feature"); } };
  const handleComment = async (postId, text) => { try { await api.post(`/feed/${postId}/comment`, { text }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const handleDeleteComment = async (postId, commentId) => { try { await api.delete(`/feed/${postId}/comment/${commentId}`); load(); } catch { toast.error("Failed to delete"); } };

  return (
    <Layout>
      <div data-testid="studio-feed" className="space-y-6 animate-fade-in-up max-w-2xl mx-auto">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#5C3A2E] font-semibold tracking-tight">Studio Feed</h1>
          <p className="text-[#2B2B2B]/50 mt-1 text-sm">Share your creative journey</p>
        </div>

        {/* Composer */}
        {canCreatePost && <PostComposer onPost={handlePost} posting={posting} />}

        {/* Feed */}
        {loading ? (
          <div className="space-y-4">{[1,2].map((i) => <div key={`load-${i}`} className="h-48 shimmer rounded-2xl" />)}</div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-12 h-12 text-[#EADBC8] mx-auto mb-3" />
            <p className="text-[#2B2B2B]/40 text-sm">No posts yet. Share something with the studio!</p>
          </div>
        ) : (
          <div className="space-y-5">
            {posts.map((p) => (
              <PostCard key={p.id} post={p} currentUserId={user?.id} isAdmin={isAdmin} userRole={user?.role}
                onLike={handleLike} onDelete={handleDelete} onFeature={handleFeature} onShare={setSharePost}
                onComment={handleComment} onDeleteComment={handleDeleteComment} />
            ))}
          </div>
        )}

        <ShareDialog open={!!sharePost} onClose={() => setSharePost(null)} post={sharePost} />
      </div>
    </Layout>
  );
}
